// AAL-86 ticket 14: where the in-form accept/revert controls go. Pure: the overlay component
// (components/copilot/CopilotFieldControls.vue) hands in the review entries, a lookup from entry to
// field element (the page adapter's copilotFieldElement) and the element rectangles.

import type { ReviewEntry } from './proposal-apply'

export type ControlGroup<E = unknown> = {
	/** stable key for rendering: the ids of the group's entries */
	key: string
	element: E
	/** the open entries shown at this element, in entry order */
	entries: ReviewEntry[]
	/** at least one entry is a pending suggestion (old -> new in the tooltip) */
	pending: boolean
}

export type Placement = { top: number, left: number }
export type Viewport = { topInset: number, viewportHeight: number }
type Rect = Pick<DOMRect, 'top' | 'right' | 'bottom' | 'width' | 'height'>

const OPEN = [ 'proposed', 'pending' ]
/** the controls sit on the field's top edge, left of the `Copilot` badge of `.copilot-proposed` */
const LIFT = 12
const BADGE_GAP = 72

/**
 * One group per field element for the entries the user can still decide on (proposed, pending).
 * Several entries can share an element (fields of one ticket option); they are decided together.
 * Entries whose field is not on the page (hidden section, other step) get no controls.
 */
export function controlGroups<E>(entries: ReviewEntry[], locate: (entry: ReviewEntry) => E | null | undefined): ControlGroup<E>[] {
	const groups: ControlGroup<E>[] = []
	for (const entry of entries) {
		if (!OPEN.includes(entry.status)) continue
		const element = locate(entry)
		if (!element) continue
		let group = groups.find(g => g.element === element)
		if (!group) groups.push(group = { key: '', element, entries: [], pending: false })
		group.entries.push(entry)
		group.pending ||= entry.status === 'pending'
	}
	for (const group of groups) group.key = group.entries.map(e => e.id).join('-')
	return groups
}

/**
 * Viewport position of a group's controls for the field rectangle, or null when the controls are not
 * visible: field not rendered (zero size), below the viewport, or its top edge (where the controls
 * sit) scrolled under the fixed header (`topInset`). The controls move with the field's top edge and
 * never stick to the header.
 */
export function placeControls(rect: Rect, { topInset, viewportHeight }: Viewport): Placement | null {
	if (!rect.width && !rect.height) return null
	const top = rect.top - LIFT
	if (top < topInset || rect.top >= viewportHeight) return null
	return { top, left: rect.right - BADGE_GAP }
}
